import React, { useEffect, useState } from "react";
import axios from "axios";
import { MegaphoneIcon, XMarkIcon, PlusIcon } from "@heroicons/react/24/outline";
import { SERVER_URL } from "../config";

/* ── shared input style (matches AdminAttendanceOverview light theme) ────────── */
const inputStyle =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors";

/* ════════════════════════════════════════════════════════════════════════════ */
const AnnouncementComponent = ({ employee }) => {

  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading]             = useState(false);
  const [showModal, setShowModal]         = useState(false);

  const [newAnnouncement, setNewAnnouncement] = useState({
    title: "",
    message: ""
  });

  /* ---------------- FETCH ANNOUNCEMENTS ---------------- */
  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${SERVER_URL}/Rahul/announcementHistory.php`);
      if (res.data.status === "success") setAnnouncements(res.data.announcements);
      setLoading(false);
    } catch (err) {
      console.error("Fetch announcement error:", err);
      setLoading(false);
    }
  };

  useEffect(() => { fetchAnnouncements(); }, []);

  /* ---------------- CREATE ANNOUNCEMENT ---------------- */
  const handleCreateAnnouncement = async () => {
    if (!newAnnouncement.title || !newAnnouncement.message) {
      alert("Please fill all fields");
      return;
    }
    try {
      const formData = new FormData();
      formData.append("title", newAnnouncement.title);
      formData.append("message", newAnnouncement.message);
      formData.append("employee_id", employee.emp_id);
      formData.append("employee_name", employee.employee_name);
      const res = await axios.post(`${SERVER_URL}/Rahul/insertAnnouncement.php`, formData);
      if (res.data.status === "success") {
        setShowModal(false);
        setNewAnnouncement({ title: "", message: "" });
        fetchAnnouncements();
      }
    } catch (err) {
      console.error("Create announcement error:", err);
    }
  };

  /* ════════════════════════════════ JSX ════════════════════════════════ */
  return (
    <>
      <style>{`
        .att-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
        .att-scrollbar::-webkit-scrollbar-track { background: rgba(226,232,240,0.6); border-radius:10px; }
        .att-scrollbar::-webkit-scrollbar-thumb { background: rgba(148,163,184,0.5); border-radius:10px; }
        .att-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(100,116,139,0.7); }
      `}</style>

      <div className="h-full flex flex-col p-4">

        {/* ── Header ── */}
        <div className="flex items-center justify-between mb-4 flex-shrink-0 pb-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-1 h-5 rounded-full"
              style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
            <MegaphoneIcon className="w-4 h-4 text-blue-600" />
            <h1 className="text-sm font-bold text-slate-800">
              Announcements
            </h1>
          </div>

          {(employee?.privilages) === 2 && (
            <button
              onClick={() => setShowModal(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white border-0 transition-all hover:opacity-90"
              style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
            >
              <PlusIcon className="w-3.5 h-3.5" />
              New
            </button>
          )}
        </div>

        {/* ── Scrollable list ── */}
        <div className="flex-1 overflow-y-auto att-scrollbar pr-1">
          <div className="flex flex-col gap-3">

            {loading ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <div className="w-6 h-6 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-slate-400">Loading announcements…</span>
              </div>
            ) : announcements.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 gap-2">
                <MegaphoneIcon className="w-8 h-8 text-slate-300" />
                <span className="text-sm text-slate-400">No announcements available.</span>
              </div>
            ) : (
              announcements.map((item) => (
                <div
                  key={item.slNo}
                  className="rounded-xl p-4 border border-slate-200 bg-white transition-all hover:border-blue-300 hover:bg-blue-50/60 cursor-default"
                  style={{ boxShadow: "0 1px 4px rgba(15,23,42,0.05)" }}
                >
                  <div className="flex justify-between items-start gap-2">
                    <div className="flex-1 min-w-0">

                      {/* Title with left accent dot */}
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                        <h3 className="text-sm font-bold text-slate-800 truncate">
                          {item.title}
                        </h3>
                      </div>

                      <p className="text-xs text-slate-500 mt-1 leading-relaxed pl-3.5">
                        {item.message}
                      </p>

                      <div className="flex items-center gap-1.5 mt-2 pl-3.5">
                        <span className="text-[10px] text-slate-400">Published by:</span>
                        <span className="text-[10px] font-semibold text-blue-600">{item.publishedBy}</span>
                      </div>

                    </div>

                    {/* Date badge */}
                    <span className="text-[10px] font-medium text-slate-400 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-full whitespace-nowrap shrink-0 mt-0.5">
                      {item.publishDate}
                    </span>
                  </div>
                </div>
              ))
            )}

          </div>
        </div>

        {/* ── CREATE ANNOUNCEMENT MODAL ── */}
        {showModal && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: "rgba(15,23,42,0.5)", backdropFilter: "blur(4px)" }}
          >
            <div
              className="w-full max-w-lg rounded-2xl p-6 bg-white border border-slate-200"
              style={{ boxShadow: "0 20px 60px rgba(15,23,42,0.2)" }}
            >
              {/* Modal header */}
              <div className="flex justify-between items-center mb-5 pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="w-1 h-6 rounded-full"
                    style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                  <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <MegaphoneIcon className="w-5 h-5 text-blue-600" />
                    Create Announcement
                  </h2>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Form */}
              <div className="flex flex-col gap-4">

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Title
                  </label>
                  <input
                    type="text"
                    placeholder="Announcement Title"
                    value={newAnnouncement.title}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, title: e.target.value })}
                    className={inputStyle}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Message
                  </label>
                  <textarea
                    rows="4"
                    placeholder="Announcement message..."
                    value={newAnnouncement.message}
                    onChange={(e) => setNewAnnouncement({ ...newAnnouncement, message: e.target.value })}
                    className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400 transition-colors resize-none"
                  />
                </div>

                <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
                  <button
                    onClick={() => setShowModal(false)}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateAnnouncement}
                    className="flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
                    style={{ background: "linear-gradient(135deg, #1e40af, #0369a1)" }}
                  >
                    <MegaphoneIcon className="w-4 h-4" />
                    Publish
                  </button>
                </div>

              </div>
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default AnnouncementComponent;